import '../../../../packages/neb-lit-components/src/components/neb-text';
import '../../../../packages/neb-lit-components/src/components/neb-button';

import { html, css } from 'lit';

import { APPOINTMENT_STATUS } from '../../../../packages/neb-lit-components/src/components/scheduling/neb-appointment-options';
import NebTable, {
  ELEMENTS as BASE_ELEMENTS,
} from '../../../../packages/neb-lit-components/src/components/tables/neb-table';
import { parseDate } from '../../../../packages/neb-utils/date-util';

export const TYPE = {
  FUTURE: 'future',
  PAST: 'past',
};

export const ELEMENTS = {
  ...BASE_ELEMENTS,
  message: { id: 'message' },
  text: { selector: '.neb-text' },
  viewAppointmentButtons: { selector: '[id^=view-appointment-button-]' },
  appointmentDates: { selector: '[id^=appointment-date-]', tag: 'neb-text' },
  appointmentTimes: { selector: '[id^=appointment-time-]', tag: 'neb-text' },
  appointmentTypes: { selector: '[id^=appointment-type-]', tag: 'neb-text' },
  appointmentProviders: {
    selector: '[id^=appointment-provider-]',
    tag: 'neb-text',
  },
  appointmentLocations: {
    selector: '[id^=appointment-location-]',
    tag: 'neb-text',
  },
  checkinButtons: {
    selector: '[id^=check-in-appointment-button-]',
    tag: 'neb-button',
  },
  patientNames: { selector: '[id^=patient-name-]', tag: 'neb-text' },
};

export const canCheckInAppointment = (
  { status, start },
  selfCheckInInterval,
) => {
  const intervalToHours = (selfCheckInInterval * 5) / 60;

  if (intervalToHours <= 6) {
    return (
      ['Active', 'Checked-In'].includes(status) &&
      parseDate(start).isSame(parseDate(), 'day') &&
      parseDate(start).diff(parseDate(), 'hours', true) <= intervalToHours
    );
  }

  return (
    ['Active', 'Checked-In'].includes(status) &&
    parseDate(start).isSame(parseDate(), 'day')
  );
};

class NebTableAppointments extends NebTable {
  static get properties() {
    return {
      enableSelfCheckIn: Boolean,
    };
  }

  constructor() {
    super();
    this.__initState();
    this.__initHandlers();
  }

  __initState() {
    this.selfCheckInInterval = 12;
    this.enableSelfCheckIn = true;

    this.onCheckIn = () => {};

    this.onRenderNoItems = () => {};
  }

  __initHandlers() {
    this.handlers = {
      ...this.handlers,
      viewAppointment: ({ name }) => {
        const rowIndex = Number(name.split('.')[0]);
        this.onSelectRow(this.name, this.model[rowIndex], rowIndex);
      },
      checkInAppointment: ({ name: rowIndex, event }) => {
        this.onCheckIn(this.model[rowIndex]);
        event.stopPropagation();
      },
    };
  }

  static get styles() {
    return [
      ...super.styles,
      css`
        .data-cell {
          display: flex;
          flex-direction: column;
        }

        .button-check-in {
          flex: 1 0 0;
          width: max-content;
        }

        .message {
          line-height: 1.5;
        }

        .row-data[expanded] {
          background-color: inherit;
        }

        [id^='section-expand-'] {
          display: flex;
          width: 135px;
          padding-bottom: 18px;
        }
      `,
    ];
  }

  __renderCheckInButton(index) {
    return this.enableSelfCheckIn
      ? html`
          <neb-button
            id="check-in-appointment-button-${index}"
            class="button-check-in"
            label="${this.model[index].status ===
              APPOINTMENT_STATUS.CHECKED_IN || this.model[index].arrivedAt
              ? 'Checked In'
              : 'Check In'}"
            name="${index}"
            .onClick="${this.handlers.checkInAppointment}"
            ?disabled="${this.model[index].status ===
              APPOINTMENT_STATUS.CHECKED_IN || this.model[index].arrivedAt}"
          ></neb-button>
        `
      : html``;
  }

  renderExpandedRow(index, _row, _errors) {
    return this.__renderCheckInButton(index);
  }

  renderDataCell(value, columnConfig, rowIndex, name, _error) {
    switch (columnConfig.key) {
      case 'start':
        return html`
          <div class="data-cell">
            <neb-text id="patient-name-${rowIndex}">
              ${this.model[rowIndex].patientName}
            </neb-text>

            <neb-text id="appointment-date-${rowIndex}" bold>
              ${parseDate(value).format('MMMM DD, YYYY')} </neb-text
            ><neb-text id="appointment-time-${rowIndex}">
              ${parseDate(value).format('h:mm A')}
            </neb-text>

            <neb-text id="appointment-location-${rowIndex}">
              ${this.model[rowIndex].locationName}
            </neb-text>
          </div>
        `;

      case 'appointmentTypeName':
        return this.model[rowIndex].provider
          ? html`
              <div class="data-cell">
                <neb-text id="appointment-type-${rowIndex}" bold
                  >${value}</neb-text
                ><neb-text id="appointment-provider-${rowIndex}">
                  ${this.model[rowIndex].provider.firstName}
                  ${this.model[rowIndex].provider.lastName}
                </neb-text>
              </div>
            `
          : html`
              <div class="data-cell">
                <neb-text id="appointment-type-${rowIndex}" bold
                  >${value}</neb-text
                >
              </div>
            `;

      case 'viewButton':
        return html`
          <neb-button
            id="view-appointment-button-${rowIndex}"
            label="View"
            name="${name}"
            .onClick="${this.handlers.viewAppointment}"
          ></neb-button>
        `;

      case 'checkInButton':
        return canCheckInAppointment(
          this.model[rowIndex],
          this.selfCheckInInterval,
        )
          ? this.__renderCheckInButton(rowIndex)
          : '';

      default:
        return super.renderDataCell(value, columnConfig);
    }
  }

  renderNoItems() {
    return html`
      <slot
        id="${ELEMENTS.message.id}"
        class="message"
        name="noItemsText"
      ></slot>
    `;
  }
}

customElements.define('neb-table-appointments', NebTableAppointments);
